import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import { WorkInSumTitles, WorkOrderChapterMap } from 'constantsProject';
import type {
    ChapterEnum,
    WorkInSumEnum,
    WorkOrderChangePriceItem,
    WorkOrderChangeSeasonData,
    WorkOrderChangeStatusEnum,
    WorkOrderPriceItemCodeFilterData,
    WorkOrderPriceStatusEnum,
} from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { badgePropsByStatus } from 'pages/Project/Common/CodeCell';

import type { ColDef, ColGroupDef, GridApi } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';

import { getWorkOrderUnitCellValue } from '../../../helpersUerProject';
import {
    WorkOrderChangeCodeRenderer,
    WorkOrderChangeHeaderCellWithTooltip,
    WorkOrderChangeNumericCell,
    WorkOrderChangeStatusRenderer,
} from '../../CellRenderers';
import {
    COST_COLUMNS_WIDTH,
    totalCostText,
    totalCostTooltipText,
    unitCostText,
    unitCostTooltipText,
    VOLUME_COLUMNS_WIDTH,
} from '../../constants';
import { WorkOrderChangeMenuRenderer } from '../cellRenderers';
import { codeGroupHelper, codeWhereVariablesGetter, getWhereFilterVariables } from '../helpers';
import type { WorkOrderChangeMenuActionTypes } from '../types';
import {
    uerWorkOrderChangePriceItemChapterFilterGql,
    uerWorkOrderChangePriceItemCodeFilterGql,
    uerWorkOrderChangePriceItemNameFilterGql,
    uerWorkOrderChangePriceItemStatusFilterGql,
    uerWorkOrderChangePriceItemUnitFilterGql,
    uerWorkOrderChangePriceItemWorkInSumFilterGql,
    uerWorkOrderChangePriceItemWorkNameFilterGql,
} from '../workOrderChangeQueries';

import { useWorkOrderChangeRowDataRefresh } from './useWorkOrderChangeRowDataRefresh';
import { groupNameHeaderName, useWorkOrderChangeTypeWorkColumns } from './useWorkOrderChangeTypeWorkColumns';
import { useWorkOrderChangeWorkRatioInfo } from './useWorkOrderChangeWorkRatioInfo';
import { useWorkOrderChangeSeasonsColumns } from './workOrderChangeSeasonColumns';

export const useWorkOrderChangeColumns = ({
    costRounding,
    workOrderChangeId,
    constructionTypeId,
    statusWorkOrderChange,
    isActionsDisabled,
    openModalHandler,
    workOrderChangeSeasons,
    api,
    changeFilterHandler,
    objectFilterData,
    hasWorkKind,
    refreshTableObjectsProp,
}: {
    costRounding: number;
    workOrderChangeId: number;
    constructionTypeId: number;
    statusWorkOrderChange?: WorkOrderChangeStatusEnum;
    isActionsDisabled: boolean;
    workOrderChangeSeasons: WorkOrderChangeSeasonData[];
    api: GridApi;
    openModalHandler: (type: WorkOrderChangeMenuActionTypes, rowData: WorkOrderChangePriceItem, options?: any) => void;
    changeFilterHandler: ({ fieldName, checkedValues }: { fieldName: string; checkedValues: any[] }) => void;
    objectFilterData: {
        workOrderRootObjectId: number | null | undefined;
        workOrderChangeRootObjectId: number | null | undefined;
        workOrderObjectId?: number | null;
        workOrderChangeObjectId?: number | null;
    } | null;
    hasWorkKind: boolean | null | undefined;
    refreshTableObjectsProp?: (objectIds: number[], needUpdateOnlyObj: boolean) => void;
}) => {
    const [selection, , , setSelection] = useSelection(`workOrderChangeTable_${workOrderChangeId}`);
    const [pinnedItems, , , setPinned] = usePin(`workOrderChangeTable_${workOrderChangeId}`);
    const [checkedRows, setCheckedRows] = useState({});
    const { workOrderChangeUer: workOrderChangeUerRules } = useAccess();

    const filterVariables = useMemo(
        () => ({
            workOrderChangeId,
            constructionTypeId,
            ...getWhereFilterVariables(objectFilterData),
        }),
        [constructionTypeId, objectFilterData, workOrderChangeId],
    );

    const { hasEmptyWorkRatios, refreshWorkRatioInfo } = useWorkOrderChangeWorkRatioInfo({
        constructionTypeId,
        workOrderChangeId,
    });

    const typeWorkColumns = useWorkOrderChangeTypeWorkColumns({
        statusWorkOrderChange,
        useAgCellRowDataRefresh: useWorkOrderChangeRowDataRefresh,
        useAgCellRowDataRefreshParams: {
            workOrderChangeId,
            constructionTypeId,
        },
        hasEmptyWorkRatios,
        onWorkRatioEditSuccess: refreshWorkRatioInfo,
    });

    const seasonColumns = useWorkOrderChangeSeasonsColumns({
        workOrderChangeId,
        constructionTypeId,
        isActionsDisabled,
        api,
        workOrderChangeUerRules,
        costRounding,
        workOrderChangeSeasons,
        refreshTableObjectsProp,
    });

    const checkRowHandler = useCallback(({ id, data }) => {
        setCheckedRows((prev) => ({ ...prev, [id]: data }));
    }, []);

    const resetCheckedRows = useCallback(() => {
        setCheckedRows({});
    }, []);

    const columns = useMemo<(ColDef<WorkOrderChangePriceItem> | ColGroupDef<WorkOrderChangePriceItem>)[]>(
        () => [
            {
                headerName: '',
                field: '' as any,
                width: 80,
                cellRenderer: WorkOrderChangeMenuRenderer,
                cellRendererParams: {
                    openModalHandler,
                    isActionsDisabled,
                    checkedRows,
                    checkRowHandler,
                },
                cellClass: 'centeredAgGridCell firstColumn',
                pinned: 'left',
                lockPosition: 'left',
            },

            {
                headerName: 'Объект',
                field: 'rootObjectName',
                flex: 1,
                minWidth: 150,
            },
            {
                headerName: 'Подобъект',
                field: 'objectName',
                flex: 1,
                minWidth: 150,
            },
            {
                headerName: 'Раздел',
                field: 'chapter',
                flex: 1,
                minWidth: 150,
                valueFormatter: ({ value }: { value: ChapterEnum | undefined }) =>
                    value ? WorkOrderChapterMap[value] : '',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderChangePriceItemChapterFilter',
                    query: uerWorkOrderChangePriceItemChapterFilterGql,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Раздел',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                            valueFormatter: ({ value }: { value: ChapterEnum | undefined }) =>
                                value ? WorkOrderChapterMap[value] : '',
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Статус',
                field: 'status',
                width: 105,
                cellRenderer: WorkOrderChangeStatusRenderer,
                cellClass: 'centeredAgGridCell',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderChangePriceItemStatusFilter',
                    query: uerWorkOrderChangePriceItemStatusFilterGql,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Статус',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                            cellRenderer: ({ value }: { value: WorkOrderPriceStatusEnum }) =>
                                badgePropsByStatus[value].label,
                        },
                    ],
                    changeFilterHandler,
                },
                tooltipComponent: null,
            },
            {
                headerName: 'Шифр',
                field: 'code',
                cellRenderer: WorkOrderChangeCodeRenderer,
                width: 150,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderChangePriceItemCodeFilter',
                    query: uerWorkOrderChangePriceItemCodeFilterGql,
                    childFieldAccessor: 'value',
                    columns: [
                        { headerName: '', lockVisible: true, checkboxSelection: true, width: 50 },
                        {
                            headerName: 'Шифр/УЕР',
                            field: 'parentCode',
                            lockVisible: true,
                            flex: 1,
                            cellRenderer: ({
                                data,
                            }: {
                                data: { value: WorkOrderPriceItemCodeFilterData & { isParent: boolean } };
                            }) => (data?.value?.isParent ? data?.value?.uerCode ?? '' : ''),
                        },
                        {
                            headerName: 'МТР/Коэффициент',
                            field: 'code',
                            lockVisible: true,
                            flex: 1,
                            cellRenderer: ({
                                data,
                            }: {
                                data: { value: WorkOrderPriceItemCodeFilterData & { isParent: boolean } };
                            }) => (!data?.value?.isParent ? data.value.code ?? '' : ''),
                        },
                    ],
                    changeFilterHandler,
                    groupAndSortItemsHelper: codeGroupHelper,
                    customWhereVariablesGetter: codeWhereVariablesGetter,
                },
            },
            {
                headerName: 'Наименование ',
                field: 'name',
                resizable: true,
                width: 300,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderChangePriceItemNameFilter',
                    query: uerWorkOrderChangePriceItemNameFilterGql,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Ед.изм',
                field: 'unit',
                width: 110,
                filter: AgGridCustomFilterAdapter,
                valueGetter: getWorkOrderUnitCellValue,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderChangePriceItemUnitFilter',
                    query: uerWorkOrderChangePriceItemUnitFilterGql,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Ед.изм',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Стоимость единицы в текущем уровне цен, руб. без НДС',
                headerTooltip: '',
                headerComponent: WorkOrderChangeHeaderCellWithTooltip({
                    text: unitCostText,
                    tooltipText: unitCostTooltipText,
                }),
                field: 'unitCost',
                cellRenderer: WorkOrderChangeNumericCell({
                    key: 'unitCost',
                    ignoreRoot: true,
                    ignoreOther: true,
                    ignoreOtherGroup: true,
                }),
            },
            {
                headerName: 'План по НЗ',
                spanHeaderHeight: true,
                marryChildren: true,
                children: [
                    {
                        headerName: 'Объем всего',
                        field: 'totalPlanVolume',
                        width: VOLUME_COLUMNS_WIDTH,
                        cellRenderer: WorkOrderChangeNumericCell({
                            key: 'totalPlanVolume',
                            decimal: 3,
                            maxDecimal: 6,
                            ignoreRoot: true,
                            ignoreOther: true,
                            ignoreOtherGroup: true,
                        }),
                    },
                    {
                        headerName: 'Стоимость всего, руб. без НДС',
                        field: 'totalPlanCost',
                        headerComponent: WorkOrderChangeHeaderCellWithTooltip({
                            text: totalCostText,
                            tooltipText: totalCostTooltipText,
                        }),
                        width: COST_COLUMNS_WIDTH,
                        cellRenderer: WorkOrderChangeNumericCell({
                            key: 'totalPlanCost',
                            decimal: costRounding,
                            ignoreOtherGroup: true,
                        }),
                    },
                ],
            },
            {
                headerName: 'Выполнено по НЗ',
                marryChildren: true,
                children: [
                    {
                        headerName: 'Объем всего',
                        field: 'totalCompletedVolume',
                        width: VOLUME_COLUMNS_WIDTH,
                        cellRenderer: WorkOrderChangeNumericCell({
                            key: 'totalCompletedVolume',
                            decimal: 3,
                            maxDecimal: 6,
                            ignoreRoot: true,
                            ignoreOther: true,
                            ignoreOtherGroup: true,
                        }),
                    },
                    {
                        headerName: 'Стоимость всего, руб. без НДС',
                        field: 'totalCompletedCost',
                        headerComponent: WorkOrderChangeHeaderCellWithTooltip({
                            text: totalCostText,
                            tooltipText: totalCostTooltipText,
                        }),
                        width: COST_COLUMNS_WIDTH,
                        cellRenderer: WorkOrderChangeNumericCell({
                            key: 'totalCompletedCost',
                            decimal: costRounding,
                            ignoreOtherGroup: true,
                        }),
                    },
                ],
            },
            {
                headerName: 'Остаток по НЗ',
                marryChildren: true,
                children: [
                    {
                        headerName: 'Объем всего',
                        field: 'totalRemainderVolume',
                        width: VOLUME_COLUMNS_WIDTH,
                        cellRenderer: WorkOrderChangeNumericCell({
                            key: 'totalRemainderVolume',
                            decimal: 3,
                            maxDecimal: 6,
                            ignoreRoot: true,
                            ignoreOther: true,
                            ignoreOtherGroup: true,
                        }),
                    },
                    {
                        headerName: 'Стоимость всего, руб. без НДС',
                        field: 'totalRemainderCost',
                        headerComponent: WorkOrderChangeHeaderCellWithTooltip({
                            text: totalCostText,
                            tooltipText: totalCostTooltipText,
                        }),
                        width: COST_COLUMNS_WIDTH,
                        cellRenderer: WorkOrderChangeNumericCell({
                            key: 'totalRemainderCost',
                            decimal: costRounding,
                            ignoreOtherGroup: true,
                        }),
                    },
                ],
            },
            ...(typeWorkColumns && hasWorkKind
                ? (typeWorkColumns as any[]).map((typeCol: ColGroupDef) => ({
                      ...typeCol,
                      children: typeCol.children?.map((child: ColDef) => {
                          if (child?.field === 'workName') {
                              return {
                                  ...child,
                                  width: 165,
                                  filter: AgGridCustomFilterAdapter,
                                  filterParams: {
                                      hideTableHeader: true,
                                      variables: filterVariables,
                                      dataAccessor: 'uerWorkOrderChangePriceItemWorkNameFilter',
                                      query: uerWorkOrderChangePriceItemWorkNameFilterGql,
                                      childFieldAccessor: 'value',
                                      whereVariables: ['value'],
                                      columns: [
                                          {
                                              headerName: 'Наименование',
                                              field: 'value',
                                              checkboxSelection: true,
                                              lockVisible: true,
                                              flex: 1,
                                          },
                                      ],
                                      changeFilterHandler,
                                  },
                              };
                          }
                          if (child?.field === 'workInSum') {
                              return {
                                  ...child,
                                  headerClass: (child.headerClass as string)?.replace('noWrap', ''),
                                  width: 235,
                                  filter: AgGridCustomFilterAdapter,
                                  filterParams: {
                                      withSearch: false,
                                      hideTableHeader: true,
                                      variables: filterVariables,
                                      dataAccessor: 'uerWorkOrderChangePriceItemWorkInSumFilter',
                                      query: uerWorkOrderChangePriceItemWorkInSumFilterGql,
                                      childFieldAccessor: 'value',
                                      whereVariables: ['value'],
                                      columns: [
                                          {
                                              headerName: 'Участие в суммировании ФО по виду работ',
                                              field: 'value',
                                              checkboxSelection: true,
                                              lockVisible: true,
                                              flex: 1,
                                              valueFormatter: ({ value }: { value: WorkInSumEnum }) =>
                                                  WorkInSumTitles[value],
                                          },
                                      ],
                                      changeFilterHandler,
                                  },
                              };
                          }

                          return child;
                      }),
                  }))
                : []),
            ...(seasonColumns as any[]),
        ],
        [
            changeFilterHandler,
            checkRowHandler,
            checkedRows,
            costRounding,
            filterVariables,
            hasWorkKind,
            isActionsDisabled,
            openModalHandler,
            seasonColumns,
            typeWorkColumns,
        ],
    );

    const isColumnVisible = useCallback(
        (id: string) => {
            if (id === groupNameHeaderName) {
                return selection[id] ?? false;
            }

            return selection[id] ?? true;
        },
        [selection],
    );
    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                if (idx > 0) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean };

                    if (idx < 3 || (idx > 4 && idx < 9)) {
                        newColumn.disabled = true;
                    }

                    acc.push(newColumn);

                    return acc;
                }

                return acc;
            }, []),
        [columns],
    );

    const tableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setSelection,
                setPinned,
            });
        },
        [fullSetSettingsColumns, setPinned, setSelection],
    );

    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    return {
        columns: fullSetColumns,
        fullSetSettingsColumns,
        isColumnVisible,
        tableSettingsConfirm,
        checkedRows,
        resetCheckedRows,
    };
};
